// Which rate limit stopped a request, until when, and how to say so.
//
// GitHub has two kinds (docs.github.com, "Rate limits for the REST API" and
// "for the GraphQL API"):
// - the primary limit: an hourly budget per account (5,000 points an hour
//   for GraphQL, 5,000 requests for REST), shared by every app the account
//   uses. Its responses carry `X-RateLimit-Limit/-Used/-Remaining/-Reset`;
//   when it is used up, `Remaining` is 0 and `Reset` is when it refills.
// - secondary limits: short holds for too many requests at once or too much
//   GitHub server time in a minute, whatever the hourly budget says. GitHub
//   answers 403 or 429 "You have exceeded a secondary rate limit", usually
//   with `Retry-After`.
// Changeling's server also has its own per-user limit (429 `rate_limited`
// with `Retry-After`).

/** GitHub's hourly budget as its `X-RateLimit-*` headers reported it. */
export interface Budget {
  /** `graphql` (points), `core` (REST requests), `search`, …; null when not sent. */
  resource: string | null;
  limit: number;
  used: number;
  remaining: number;
  /** When the budget refills (epoch ms). */
  resetAt: number;
}

/** Whose limit: GitHub's hourly budget, a GitHub secondary limit, or Changeling's server. */
export type RateLimitKind = 'primary' | 'secondary' | 'changelist';

/** A limit in force: calls to GitHub wait until `until`. */
export interface RateLimit {
  kind: RateLimitKind;
  /** When calls resume (epoch ms). */
  until: number;
  /** Whether the answer said when (`X-RateLimit-Reset` or `Retry-After`); false: a default wait. */
  stated: boolean;
  /** The hourly budget, when known (from this answer or the latest one that carried it). */
  budget: Budget | null;
}

function limitText(l: RateLimit): string {
  const at = clockTime(l.until);
  switch (l.kind) {
    case 'primary':
      return `GitHub's hourly rate limit is used up; Changeling will try again at ${at}`;
    case 'secondary':
      return `GitHub is briefly limiting requests; Changeling will try again at ${at}`;
    case 'changelist':
      return `Changeling's server is limiting requests; it will try again at ${at}`;
  }
}

/**
 * A call refused for a rate limit: `limit` says which, and until when.
 * `noted`: the scheduler already counted this limit (it was noted on the
 * way up, as `noteServerLimit` does), so meeting it again counts nothing.
 */
export class RateLimitError extends Error {
  constructor(
    public limit: RateLimit,
    public noted = false,
  ) {
    super(limitText(limit));
  }
}

/** How long to wait when a limit was hit and the answer did not say for how long. */
export const DEFAULT_WAIT_MS = 60_000;

/**
 * How far the answering server's clock reads ahead of this device's (ms),
 * by the response's `Date` header plus its `Age` (an answer a cache kept
 * was dated when it was made); 0 without a date. GitHub's reset times are
 * on the server's clock, so a device whose clock is off would otherwise
 * wait too long, or too little, for a reset.
 */
export function serverOffset(headers: Headers, now = Date.now()): number {
  const date = headers.get('date');
  const at = date ? Date.parse(date) : NaN;
  if (Number.isNaN(at)) return 0;
  const age = Number(headers.get('age'));
  return at + (Number.isFinite(age) && age > 0 ? age * 1000 : 0) - now;
}

/** The `X-RateLimit-*` budget a response reported, its reset on this device's clock; or null. */
export function budgetOf(headers: Headers, now = Date.now()): Budget | null {
  const n = (name: string) => {
    const v = headers.get(`x-ratelimit-${name}`);
    const x = v === null ? NaN : Number(v);
    return Number.isFinite(x) ? x : null;
  };
  const limit = n('limit');
  const remaining = n('remaining');
  const reset = n('reset');
  if (limit === null || remaining === null || reset === null) return null;
  return {
    resource: headers.get('x-ratelimit-resource'),
    limit,
    used: n('used') ?? limit - remaining,
    remaining,
    resetAt: reset * 1000 - serverOffset(headers, now),
  };
}

const SECONDARY = /secondary rate limit|abuse/i;
const PRIMARY = /api rate limit exceeded/i;

/** What an error answer says about rate limits. */
export interface LimitAnswer {
  status: number;
  body: unknown;
  /** `Retry-After` in seconds. */
  retryAfter: number | null;
  /** When a used-up budget refills (`X-RateLimit-Reset` with `Remaining: 0`), epoch ms. */
  resetAt?: number | null;
  /** The budget this answer's own headers reported: it alone may say the hourly limit is used up. */
  budget: Budget | null;
  /** A budget of the same resource from an earlier answer, used only to describe the limit. */
  known?: Budget | null;
}

/**
 * The limit an error answer reports, or null when it is no rate limit.
 * Changeling's own is 429 `rate_limited`. GitHub's primary limit is a used-up
 * budget (`Remaining: 0`) or "API rate limit exceeded"; any other 429, or a
 * 403 with `Retry-After` or a message about a rate limit, is a secondary one.
 */
export function classifyLimit(a: LimitAnswer, now = Date.now()): RateLimit | null {
  const text = JSON.stringify(a.body ?? '').toLowerCase();
  const own = a.status === 429 && typeof a.body === 'object' && a.body !== null && (a.body as { error?: unknown }).error === 'rate_limited';
  const resetAt = a.resetAt ?? (a.budget?.remaining === 0 ? a.budget.resetAt : null);
  const exhausted = resetAt !== null;
  const limited = a.status === 429 || (a.status === 403 && (a.retryAfter !== null || exhausted || text.includes('rate limit')));
  if (!limited) return null;
  const kind: RateLimitKind = own ? 'changelist' : exhausted || (PRIMARY.test(text) && !SECONDARY.test(text)) ? 'primary' : 'secondary';
  return waitFor(kind, a.retryAfter, kind === 'primary' ? resetAt : null, a.budget ?? a.known ?? null, now);
}

/**
 * The limit a GraphQL `RATE_LIMITED` error reports: the primary limit when
 * the answer's own budget is used up (`resetAt` is then its
 * `X-RateLimit-Reset`), or, without a budget, when its message says "API rate
 * limit exceeded"; else a secondary limit. `known` describes it when the
 * answer carried no budget.
 */
export function graphqlLimit(
  budget: Budget | null,
  resetAt: number | null,
  message: string,
  known: Budget | null = null,
  now = Date.now(),
): RateLimit {
  const reset = resetAt ?? (budget?.remaining === 0 ? budget.resetAt : null);
  const primary = reset !== null || (!budget && PRIMARY.test(message) && !SECONDARY.test(message));
  return waitFor(primary ? 'primary' : 'secondary', null, primary ? reset : null, budget ?? known, now);
}

function waitFor(kind: RateLimitKind, retryAfter: number | null, resetAt: number | null, budget: Budget | null, now: number): RateLimit {
  const reset = resetAt !== null && resetAt > now ? resetAt : null;
  const after = retryAfter === null ? null : now + 1000 * retryAfter;
  if (reset === null && after === null) return { kind, until: now + DEFAULT_WAIT_MS, stated: false, budget };
  return { kind, until: Math.max(reset ?? 0, after ?? 0), stated: true, budget };
}

/** A wait in plain units: "45 s", "4 min 12 s", "1 h 3 min". */
export function inDuration(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

/** A time of day to the second, in the viewer's locale ("12:49:32 PM"). */
export function clockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString();
}

/** A time of day to the minute, in the viewer's locale ("12:49 PM"). */
export function shortTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const count = (n: number) => n.toLocaleString('en-US');
const unit = (b: Budget) => (b.resource === 'graphql' ? 'points' : 'requests');

/** What a rate limit means: what was hit, when it lifts (live: depends on `now`), and context. */
export interface LimitWords {
  summary: string;
  /** When calls resume and that they resume by themselves; "Trying again now…" once due. */
  next: string;
  advice: string | null;
}

/**
 * `retries`: whether the view showing it tries again by itself when the
 * limit lifts; only then does it say so. Otherwise it says when trying
 * again can work.
 */
export function describeRateLimit(l: RateLimit, now = Date.now(), retries = false): LimitWords {
  const due = l.until <= now;
  const when = `${clockTime(l.until)}, in ${inDuration(l.until - now)}`;
  const b = l.budget;
  const unstated = l.stated ? '' : "GitHub didn't say for how long. ";
  const at = retries ? `Changeling will try again by itself at ${when}.` : `You can try again at ${when}.`;
  const now_ = retries ? 'Trying again now…' : 'You can try again now.';
  switch (l.kind) {
    case 'primary':
      return {
        summary: b
          ? `GitHub’s hourly limit for your account is used up (${count(b.used)} of ${count(b.limit)} ${unit(b)} used, ${count(b.remaining)} left).`
          : 'GitHub’s hourly limit for your account is used up.',
        next: due
          ? now_
          : l.stated
            ? `It resets at ${when}. ${retries ? 'Changeling will try again then by itself.' : 'You can try again then.'}`
            : `GitHub didn't say when it resets. ${at}`,
        advice: 'The limit is shared by every app that uses your GitHub account.',
      };
    case 'secondary':
      return {
        summary:
          'GitHub is briefly holding back requests from your account: too many requests, or too much GitHub server time, in a short while (a secondary limit).',
        next: due ? now_ : `${unstated}${at}`,
        advice: b && b.remaining > 0 ? `Your hourly limit is fine: ${count(b.remaining)} of ${count(b.limit)} ${unit(b)} left.` : null,
      };
    case 'changelist':
      return {
        summary: 'Changeling’s server is limiting how many requests you can make at once.',
        next: due ? now_ : at,
        advice: null,
      };
  }
}
